<template>
  <button
    :class="classObject"
    :disabled="props.disabled"
    :type="props.type || 'button'"
  >
    <div v-if="$slots['left-icon']" :class="$style.leftIcon">
      <slot name="left-icon"></slot>
    </div>
    <UiKitTypography
      :class="$style.text"
      :type="
        props.typographyType || (props.big ? MAP.BUTTON01D : MAP.BUTTON02D)
      "
      :style="props.disabled ? {} : textStyleObj"
      ><slot></slot
    ></UiKitTypography>
    <div
      v-if="$slots['right-icon'] && !props.disabled"
      :class="$style.rightIcon"
    >
      <slot name="right-icon"></slot>
    </div>
  </button>
</template>

<script setup>
import { computed, useCssModule, defineProps } from "vue";
import UiKitTypography from "@/ui-kit/Typography.vue";
const $style = useCssModule();
import { MAP } from "@/constants/typography";

const props = defineProps([
  "big",
  "variant",
  "active",
  "color",
  "fullwidth",
  "typographyType",
  "disabled",
  "type",
]);

const classObject = computed(() => ({
  [$style.button]: true,
  [$style.small]: !props.big,
  [$style.big]: !!props.big,
  [$style.regular]: props.variant === "regular" || !props.variant,
  [$style.outlined]: props.variant === "outlined",
  [$style.white]: props.variant === "white",
  [$style.active]: props.active,
  [$style.whiteText]: props.color === "white" || props.variant === "black",
  [$style.black]: props.variant === "black",
  [$style.fullwidth]: props.fullwidth,
}));

const textStyleObj = computed(() => ({
  color: props.color || (props.variant === "black" ? "var(--HP-white)" : null),
}));
</script>

<style module lang="scss">
.leftIcon,
.rightIcon {
  position: relative;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;

  &:empty {
    display: none;
  }
}

.text {
  text-wrap: nowrap;
}

.button {
  background: transparent;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s, color 0.2s;
  width: fit-content;
  flex-shrink: 0;
  justify-content: center;
  overflow: hidden;

  &:disabled {
    opacity: 0.7;
    pointer-events: none;
    user-select: none;
    cursor: default;

    &:hover {
      box-shadow: none;
    }
  }
}
.small {
  height: 32px;
  padding: 0px 16px;
  border-radius: 16px;

  @media screen and (max-width: 1030px) {
    height: 32px;
  }
}

.big {
  height: 56px;
  padding: 0px 24px;
  border-radius: 8px;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.regular {
  border: none;
}

.regular:hover {
  background: var(--HP-button-hover-gray);
}

.regular:active,
.regular.active {
  background: var(--HP-button-click-gray);
}

.outlined {
  outline: 2px solid #050505;

  &.whiteText {
    border-color: #fff;
  }
}

.outlined:hover {
  background: #050505;

  &.whiteText {
    background: rgba(5, 5, 5, 0.08);
  }

  .text {
    color: #fff;
  }

  .leftIcon,
  .rightIcon {
    // filter: invert(1);
  }
}

.outlined:active,
.outline.active {
  background: #2d2d2d;
}

.white {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(24px);
}
.white:hover {
  background: var(--HP-grey-01);
}

.white:active,
.white.active {
  background: var(--HP-grey-02);
}

.whiteText {
  .leftIcon,
  .rightIcon {
    // filter: invert(1);
  }
}

.fullwidth {
  width: 100%;
}

.black {
  background: #050505;
  color: #fff;

  .leftIcon,
  .rightIcon {
    // filter: invert(1);
  }
}

.big:hover {
  box-shadow: 0px 4px 8px 0px rgba(5, 5, 5, 0.24);

  .rightIcon {
    transform: translateX(8px);
  }

  &.black {
    background: #191919;
  }

  &.outlined {
    background: var(--HP-black);
  }

  &.regular {
    box-shadow: none;
  }
}

.big:active {
  .rightIcon {
    transform: translateX(100px);
    opacity: 0;
  }

  &.black {
    background: #2d2d2d;
  }

  &.outlined {
    background: var(--HP-grey-07);
    border-color: var(--HP-grey-07);
  }
}

.big:disabled {
  outline: 2px solid rgba(5, 5, 5, 0.08) !important;
  background: rgba(255, 255, 255, 0.24) !important;
  opacity: 1;

  .text {
    color: rgba(5, 5, 5, 0.16);
  }

  .leftIcon,
  .rightIcon {
    // filter: invert(0);
  }
}
</style>
